import {
  ClipboardDocumentCheckIcon,
  FolderIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Dropdown } from '@/components/Dropdown';
import { Date as DateInput } from '@/components/form/Date';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Spinner } from '@/components/ui/spinner';
import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { ParkedDomain } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';

const ToggleSwitch = ({
  checked,
  disabled,
  onChange,
  id,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  id: string;
}) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out',
        'focus:ring-chariot-brand-primary focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
        disabled ? 'opacity-50' : 'cursor-pointer',
        checked ? 'bg-chariot-brand-primary' : 'bg-chariot-text-disabled'
      )}
      data-testid={`toggle-${id}`}
    >
      <span
        className={cn(
          'inline-block h-3 w-3 transform rounded-full transition-transform duration-200 ease-in-out',
          'bg-white shadow-xs',
          checked ? 'translate-x-5' : 'translate-x-1'
        )}
      />
    </button>
  );
};

interface ParkedDomainActionsProps {
  parkedDomain: ParkedDomain;
  className?: string;
  onUpdate?: () => void | Promise<unknown>;
  /**
   * Callback for actions that affect Mailgun (burn, archive).
   * Called with a 3-second delay to allow Mailgun to update.
   */
  onMailgunUpdate?: () => void | Promise<unknown>;
  /**
   * Callback for when DNS records should be refreshed (e.g., after unparking)
   */
  onDnsUpdate?: () => void | Promise<unknown>;
  /**
   * Callback when a parking job is initiated with the target category
   */
  onParkingInitiated?: (category: string) => void;
  autoRenew: boolean;
  isTogglingAutoRenew: boolean;
  onAutoRenewToggle: (newValue: boolean) => void;
  /**
   * Whether a parking job is currently active (queued or running)
   */
  isParkingJobActive?: boolean;
}

/**
 * Convert ISO date string to YYYY-MM-DD format for date input
 */
function toDateInputFormat(isoString: string | undefined | null): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

/**
 * ParkedDomainActions
 *
 * Action buttons for parked domain management with dropdowns for:
 * - Parked Category: Unpark, Finance, Health, Legal, Business
 * - Managed: Mark available, Mark in-use, Burn, Reserve, Archive
 */
export function ParkedDomainActions({
  parkedDomain,
  className,
  onUpdate,
  onMailgunUpdate,
  onDnsUpdate,
  onParkingInitiated,
  autoRenew,
  isTogglingAutoRenew,
  onAutoRenewToggle,
  isParkingJobActive = false,
}: ParkedDomainActionsProps) {
  const axios = useAxios();
  const [showAutoRenewExpiredTooltip, setShowAutoRenewExpiredTooltip] =
    useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Check if domain has expired
  const isExpired = parkedDomain.expires
    ? new Date(parkedDomain.expires) < new Date()
    : false;

  // Disable parked category dropdown when parking job is active or domain expired
  const isCategoryDisabled = isExpired || isParkingJobActive;

  // Dialog state for Checking out a domain
  const [isInUseDialogOpen, setIsInUseDialogOpen] = useState(false);
  const [inUseFormData, setInUseFormData] = useState({
    checkoutStart: '',
    checkoutEnd: '',
    checkoutNote: '',
  });

  // Parked Category dropdown options
  const categoryOptions: DropdownOption[] = [
    {
      id: 'unpark',
      label: 'Unpark',
      value: 'unpark',
      onClick: () => handleCategoryChange('unpark'),
    },
    {
      id: 'finance',
      label: 'Finance',
      value: 'finance',
      onClick: () => handleCategoryChange('finance'),
    },
    {
      id: 'health',
      label: 'Health',
      value: 'health',
      onClick: () => handleCategoryChange('health'),
    },
    {
      id: 'legal',
      label: 'Legal',
      value: 'legal',
      onClick: () => handleCategoryChange('legal'),
    },
    {
      id: 'business',
      label: 'Business',
      value: 'business',
      onClick: () => handleCategoryChange('business'),
    },
  ];

  // Managed dropdown options
  const managedOptions: DropdownOption[] = [
    {
      id: 'available',
      label: 'Mark As Available',
      value: 'available',
      onClick: () => handleStatusChange('available'),
    },
    {
      id: 'in-use',
      label: 'Checkout',
      value: 'in-use',
      onClick: () => {
        // Open dialog and prefill with existing data if available
        setInUseFormData({
          checkoutStart: toDateInputFormat(parkedDomain.checkout_start),
          checkoutEnd: toDateInputFormat(parkedDomain.checkout_end),
          checkoutNote: parkedDomain.checkout_note || '',
        });
        setIsInUseDialogOpen(true);
      },
    },
    {
      id: 'burn',
      label: 'Burn',
      value: 'burn',
      onClick: () => handleStatusChange('burned'),
    },
    {
      id: 'reserve',
      label: 'Reserve',
      value: 'reserve',
      onClick: () => handleStatusChange('reserved'),
    },
    {
      id: 'archive',
      label: 'Archive',
      value: 'archive',
      onClick: () => handleStatusChange('archived'),
    },
  ];

  // Handler for category change
  const handleCategoryChange = async (category: string) => {
    // Map UI labels to backend values
    const categoryMap: Record<string, string> = {
      unpark: 'none',
      legal: 'legal',
      business: 'business',
      finance: 'finance',
      health: 'health',
    };

    const backendCategory = categoryMap[category];

    try {
      await axios.put('/red-team/domain-parking', {
        key: parkedDomain.key,
        parked_category: backendCategory,
      });

      // Show toast immediately after PUT succeeds, before query invalidations
      // This ensures user sees "Initiated" before any job status toasts
      if (category === 'unpark') {
        toast.success('Successfully unparked');
        // Refresh DNS records after unparking (synchronous operation)
        await onDnsUpdate?.();
      } else {
        toast.success(`Initiated parking to ${category}`);
        // Notify parent that a parking job was initiated with this category
        onParkingInitiated?.(category);
      }

      // Invalidate parking job query to start polling the new job
      await queryClient.invalidateQueries({
        queryKey: ['parking-job', parkedDomain.domain],
      });

      // Refresh drawer data to show updated information
      // Note: We don't invalidate all parked domain queries here to avoid race conditions
      // where background refetches could overwrite fresh data. The onUpdate callback
      // handles refreshing the drawer data, and the table will refresh via handleParkingJobSuccess.
      await onUpdate?.();
    } catch (error: unknown) {
      // Extract error message from backend response
      let errorMessage = 'Failed to update category';
      if (isAxiosError(error)) {
        if (error.response?.data) {
          // Backend may send a raw string or an object with message/error properties
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else {
            errorMessage =
              error.response.data.message ||
              error.response.data.error ||
              errorMessage;
          }
        }
      }
      toast.error(errorMessage);
    }
  };

  // Handler for managed status change
  const handleStatusChange = async (
    status: string,
    additionalData?: {
      checkoutStart?: string;
      checkoutEnd?: string;
      checkoutNote?: string;
    }
  ) => {
    setIsUpdatingStatus(true);
    try {
      const payload: {
        key: string;
        status: string;
        checkout_start?: string;
        checkout_end?: string;
        checkout_note?: string;
      } = {
        key: parkedDomain.key,
        status: status,
      };

      // Add additional data for checkout
      if (additionalData) {
        if (additionalData.checkoutStart) {
          payload.checkout_start = additionalData.checkoutStart;
        }
        if (additionalData.checkoutEnd) {
          payload.checkout_end = additionalData.checkoutEnd;
        }
        if (additionalData.checkoutNote) {
          payload.checkout_note = additionalData.checkoutNote;
        }
      }

      await axios.put('/red-team/domain-parking', payload);

      // Invalidate parked domains cache to refresh the list
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'parkeddomain';
        },
        refetchType: 'active',
      });

      // Refresh drawer data to show updated information
      await onUpdate?.();

      toast.success(`Usage status updated to: ${status}`);

      // For actions that affect Mailgun (burn, archive), refresh Mailgun and DNS sections
      // Mailgun takes a few seconds to update after backend deletes the domain
      if (status === 'burned' || status === 'archived') {
        onMailgunUpdate?.();
        onDnsUpdate?.();
      }
    } catch (error: unknown) {
      console.error('Failed to update usage status:', error);
      // Extract error message from backend response
      let errorMessage = 'Failed to update usage status';
      if (isAxiosError(error)) {
        if (error.response?.data) {
          // Backend may send a raw string or an object with message/error properties
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else {
            errorMessage =
              error.response.data.message ||
              error.response.data.error ||
              errorMessage;
          }
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Handler for submitting the checkout form
  const handleInUseSubmit = async () => {
    // Validate required fields
    if (!inUseFormData.checkoutStart || !inUseFormData.checkoutEnd) {
      toast.error('Start date and end date are required');
      return;
    }

    // Validate end date is after start date
    if (
      new Date(inUseFormData.checkoutEnd) <
      new Date(inUseFormData.checkoutStart)
    ) {
      toast.error('End date must be after start date');
      return;
    }

    // Format dates in UTC - end date should be inclusive (11:59:59 PM UTC)
    const checkoutStartFormatted = new Date(
      inUseFormData.checkoutStart + 'T00:00:00Z'
    ).toISOString();
    const checkoutEndFormatted = new Date(
      inUseFormData.checkoutEnd + 'T23:59:59Z'
    ).toISOString();

    await handleStatusChange('in-use', {
      checkoutStart: checkoutStartFormatted,
      checkoutEnd: checkoutEndFormatted,
      checkoutNote: inUseFormData.checkoutNote,
    });

    // Close dialog and reset form
    setIsInUseDialogOpen(false);
    setInUseFormData({
      checkoutStart: '',
      checkoutEnd: '',
      checkoutNote: '',
    });
  };

  // Tooltip text for parking job
  const parkingTooltipText =
    'Launching Netlify website. This can take between 20 seconds to a few minutes.';

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        {/* Parked Category Dropdown with optional spinner */}
        <Tooltip
          title={
            isParkingJobActive
              ? parkingTooltipText
              : isExpired
                ? 'Domain has expired'
                : ''
          }
          placement="bottom"
        >
          <div className="inline-block">
            <Dropdown
              title={isCategoryDisabled ? undefined : 'Parked Category Options'}
              size="sm"
              options={categoryOptions}
              placeholder="Update Category"
              styleType="rounded"
              border={false}
              icon={
                isParkingJobActive ? (
                  <Spinner
                    size="sm"
                    className="text-chariot-text-primary"
                    data-testid="parking-job-spinner"
                  />
                ) : (
                  <FolderIcon className="size-4" />
                )
              }
              className={cn(
                '[&_span:nth-child(3)]:hidden',
                isCategoryDisabled && 'pointer-events-none',
                isExpired && 'opacity-50' // Only dim when expired, not when parking
              )}
              listClassName="!z-[150]"
            />
          </div>
        </Tooltip>

        {/* Managed Dropdown */}
        <Tooltip
          title={
            isUpdatingStatus
              ? 'Updating usage status...'
              : isExpired
                ? 'Domain has expired'
                : ''
          }
          placement="bottom"
        >
          <div className="inline-block">
            <Dropdown
              title={
                isUpdatingStatus || isExpired
                  ? undefined
                  : 'Usage Status Options'
              }
              size="sm"
              options={managedOptions}
              placeholder="Update Status"
              styleType="rounded"
              border={false}
              icon={
                isUpdatingStatus ? (
                  <Spinner
                    size="sm"
                    className="text-chariot-text-primary"
                    data-testid="status-update-spinner"
                  />
                ) : (
                  <ClipboardDocumentCheckIcon className="size-4" />
                )
              }
              className={cn(
                '[&_span:nth-child(3)]:hidden',
                (isUpdatingStatus || isExpired) && 'pointer-events-none',
                isExpired && 'opacity-50' // Only dim when expired, not when updating
              )}
              listClassName="!z-[150]"
            />
          </div>
        </Tooltip>

        {/* Auto Renew Toggle */}
        <div
          className="relative inline-flex items-center gap-2"
          onMouseEnter={() => isExpired && setShowAutoRenewExpiredTooltip(true)}
          onMouseLeave={() => setShowAutoRenewExpiredTooltip(false)}
        >
          <span className="text-chariot-text-secondary text-xs font-medium">
            Auto Renew
          </span>
          <ToggleSwitch
            id="auto-renew-actions"
            checked={autoRenew}
            disabled={isTogglingAutoRenew || isExpired}
            onChange={onAutoRenewToggle}
          />
          {isExpired && showAutoRenewExpiredTooltip && (
            <div className="bg-chariot-background-primary border-chariot-stroke absolute top-full left-0 z-50 mt-1 w-max rounded-md border px-3 py-2 shadow-lg">
              <p className="text-chariot-text-secondary text-xs whitespace-nowrap">
                Domain has expired
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mark as In-Use Dialog */}
      <Modal
        open={isInUseDialogOpen}
        onClose={() => {
          setIsInUseDialogOpen(false);
          setInUseFormData({
            checkoutStart: '',
            checkoutEnd: '',
            checkoutNote: '',
          });
        }}
        title="Checkout the Domain"
        subtitle="Provide additional details"
        size="lg"
        footer={{
          text: 'Submit',
          onClick: handleInUseSubmit,
        }}
      >
        <div className="flex flex-col gap-4 py-4">
          {/* Start Date */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="checkoutStart"
              className="text-chariot-text-primary flex items-center gap-1.5 text-sm font-medium"
            >
              Start Date <span className="text-red-500">*</span>
              <InformationCircleIcon
                className="text-chariot-text-secondary size-4"
                title="Start date in UTC"
              />
            </label>
            <DateInput
              name="checkoutStart"
              value={inUseFormData.checkoutStart}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  checkoutStart: e.target.value,
                }))
              }
              required
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="checkoutEnd"
              className="text-chariot-text-primary flex items-center gap-1.5 text-sm font-medium"
            >
              End Date <span className="text-red-500">*</span>
              <InformationCircleIcon
                className="text-chariot-text-secondary size-4"
                title="End date in UTC, inclusive"
              />
            </label>
            <DateInput
              name="checkoutEnd"
              value={inUseFormData.checkoutEnd}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  checkoutEnd: e.target.value,
                }))
              }
              required
              min={inUseFormData.checkoutStart || undefined}
            />
          </div>

          {/* Checkout Note */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="checkoutNote"
              className="text-chariot-text-primary text-sm font-medium"
            >
              Checkout Note
            </label>
            <input
              type="text"
              name="checkoutNote"
              value={inUseFormData.checkoutNote}
              onChange={e =>
                setInUseFormData(prev => ({
                  ...prev,
                  checkoutNote: e.target.value,
                }))
              }
              placeholder="Typically customer name and SOW number"
              className={cn(
                'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
                'placeholder-chariot-text-tertiary w-full rounded-md border px-3 py-2 text-sm',
                'focus:border-chariot-brand-primary transition-colors focus:outline-hidden',
                'disabled:bg-chariot-background-tertiary disabled:cursor-not-allowed disabled:opacity-50'
              )}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
